export type EventType =
  | 'session_ready'
  | 'session_expired'
  | 'purchase_initiated'
  | 'purchase_completed'
  | 'purchase_failed'
  | 'reveal_started'
  | 'item_revealed'
  | 'item_sold'
  | 'reveal_complete'
  | 'balance_updated'
  | 'error';

export interface SessionReadyPayload {
  user_id: number;
  balance_cents: number;
}

export interface PurchaseInitiatedPayload {
  order_id: string;
}

export interface PurchaseCompletedPayload {
  order_id: string;
  items: Array<{ id: string; name: string; rarity: string }>;
}

export interface PurchaseFailedPayload {
  order_id: string;
  code?: string;
  message: string;
}

export interface RevealStartedPayload {
  order_id: string;
  item_count: number;
}

export interface ItemRevealedPayload {
  order_id: string;
  item_id: string;
  name: string;
  fair_market_value: number | null;
}

export interface ItemSoldPayload {
  order_id: string;
  item_id: string;
  gross_price: number;
}

export interface RevealCompletePayload {
  order_id: string;
}

export interface BalanceUpdatedPayload {
  balance_cents: number;
  currency: string;
}

export interface ErrorPayload {
  message: string;
  stack?: string;
}

export interface GamePlatformEvent<T extends EventType = EventType> {
  source: 'collectibles';
  type: T;
  payload?: unknown;
}

export interface EventPayloadMap {
  session_ready: SessionReadyPayload;
  session_expired: undefined;
  purchase_initiated: PurchaseInitiatedPayload;
  purchase_completed: PurchaseCompletedPayload;
  purchase_failed: PurchaseFailedPayload;
  reveal_started: RevealStartedPayload;
  item_revealed: ItemRevealedPayload;
  item_sold: ItemSoldPayload;
  reveal_complete: RevealCompletePayload;
  balance_updated: BalanceUpdatedPayload;
  error: ErrorPayload;
}

// ──────────────────────────────────────────────────────────────────────────
// Incoming events (parent casino → iframe). Envelope uses a different source
// so our own outgoing events don't echo back when someone listens to
// window.message indiscriminately.

export type IncomingEventType = 'balance_changed' | 'close_request';

export interface IncomingEvent<T extends IncomingEventType = IncomingEventType> {
  source: 'collectibles-parent';
  type: T;
}

export function isIncomingEvent(data: unknown): data is IncomingEvent {
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as { source?: unknown }).source === 'collectibles-parent' &&
    typeof (data as { type?: unknown }).type === 'string'
  );
}
